"use client";

import { useFormContext, useFieldArray } from "react-hook-form";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, PlusCircle } from "lucide-react";

import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import OptionItemRow from "./OptionItemRow";
import {
  AdditionalSelectionType,
  AdditionalItemType,
} from "@/interfaces/products";

interface OptionGroupCardProps {
  groupIndex: number;
  removeGroup: (index: number) => void;
  id: string;
}

export default function OptionGroupCard({
  groupIndex,
  removeGroup,
  id,
}: OptionGroupCardProps) {
  const { control } = useFormContext();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `additional.${groupIndex}.items`,
  });

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (active.id !== over?.id && over) {
      const oldIndex = fields.findIndex((field) => field.id === active.id);
      const newIndex = fields.findIndex((field) => field.id === over.id);
      move(oldIndex, newIndex);
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-xl border shadow-sm overflow-hidden"
    >
      <AccordionItem value={`item-${groupIndex}`} className="border-b-0">
        <div className="flex items-center bg-gray-50/70 border-b px-3 py-2.5 min-h-[48px]">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="cursor-grab p-2"
          >
            <GripVertical className="h-5 w-5 text-gray-400" />
          </button>

          <FormField
            control={control}
            name={`additional.${groupIndex}.name`}
            render={({ field }) => (
              <FormItem className="flex-grow">
                <FormControl>
                  <Input
                    placeholder="Nome do Grupo (Ex: Escolha seu queijo)"
                    {...field}
                    className="font-medium border-none bg-transparent focus-visible:ring-1 focus-visible:ring-orange-400 focus-visible:ring-offset-0 h-auto"
                    onClick={(e) => e.stopPropagation()}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {/* Badge de contagem de itens */}
          {fields.length > 0 && (
            <span className="text-xs bg-[#EE7B11] text-white px-2 py-1 rounded-full font-medium mr-2">
              {fields.length} {fields.length === 1 ? "item" : "itens"}
            </span>
          )}

          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => removeGroup(groupIndex)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>

          <AccordionTrigger className="p-2 hover:no-underline" />
        </div>

        <AccordionContent className="p-3 bg-white">
          <div className="space-y-4">
            <FormField
              control={control}
              name={`additional.${groupIndex}.selectionType`}
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>Tipo de Seleção</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={(val) =>
                        field.onChange(val as AdditionalSelectionType)
                      }
                      defaultValue={field.value}
                      className="flex flex-col sm:flex-row gap-4"
                    >
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem
                            value={AdditionalSelectionType.RADIO}
                          />
                        </FormControl>
                        <FormLabel className="font-normal">
                          Escolha Única
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem
                            value={AdditionalSelectionType.CHECKBOX}
                          />
                        </FormControl>
                        <FormLabel className="font-normal">
                          Múltipla Escolha
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem
                            value={AdditionalSelectionType.NUMBER}
                          />
                        </FormControl>
                        <FormLabel className="font-normal">
                          Quantidade
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={control}
                name={`additional.${groupIndex}.minQuantity`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mínimo</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(
                            Number.parseInt(e.target.value, 10) || 0
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`additional.${groupIndex}.maxQuantity`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Máximo</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(
                            Number.parseInt(e.target.value, 10) || 0
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-3 pt-4">
              <h4 className="font-medium text-sm text-gray-600">
                Itens do Grupo
              </h4>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={fields}
                  strategy={verticalListSortingStrategy}
                >
                  {fields.map((item, itemIndex) => (
                    <OptionItemRow
                      key={item.id}
                      id={item.id}
                      groupIndex={groupIndex}
                      itemIndex={itemIndex}
                      removeItem={() => remove(itemIndex)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full bg-transparent"
                onClick={() =>
                  append({
                    name: "",
                    unitPrice: 0,
                    unitCost: 0,
                    productId: null,
                    fileId: undefined,
                    file: undefined,
                    type: AdditionalItemType.CUSTOM,
                    consumptionFactor: 1,
                  })
                }
              >
                <PlusCircle className="mr-2 h-4 w-4" />
                Adicionar Item
              </Button>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </div>
  );
}
